import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Bell, Check, ChevronRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { fmtDateTime } from "../lib/time";
import { groupPlayerNotifications, playerNotificationLink, playerPlanGroupDate } from "../lib/playerNotifications";
import PlayerNavigation from "../components/PlayerNavigation";
import PlayerPushOptInNotice from "../components/PlayerPushOptInNotice";
import { playerNotificationDisplay, playerCommunicationTime } from "../lib/playerNotificationDisplay";
import "../player-communications.css";

export default function PlayerNotifications() {
  const { user } = useAuth(); const { t, lang } = useI18n(); const nav = useNavigate(); const location = useLocation();
  const [rows, setRows] = useState([]); const [state, setState] = useState("loading");
  const snapshotRef = useRef([]); const markedRef = useRef(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/notifications");
      setRows(data.notifications || []);
      snapshotRef.current = (data.notifications || []).filter((row) => !row.read_at).map((row) => row.id);
      markedRef.current = false; setState("ready");
    } catch { setState("error"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const markSnapshot = useCallback(async () => {
    if (markedRef.current) return;
    markedRef.current = true;
    try {
      const { data } = await api.post("/me/notifications/read-snapshot", {
        notification_ids: snapshotRef.current,
      });
      window.dispatchEvent(new CustomEvent("pc:notifications-read", {
        detail: { unreadCount: data.unread_count || 0 },
      }));
    } catch { markedRef.current = false; }
  }, []);
  useEffect(() => () => { markSnapshot(); }, [markSnapshot]);
  const leave = async (path) => { await markSnapshot(); nav(path); };
  const requestedOrigin = location.state?.notificationOrigin;
  const origin = /^\/(today|services|requests)(\?|$)/.test(requestedOrigin || "")
    ? requestedOrigin : "/today";
  const originLabel = origin.includes("tab=account")
    ? t("account") : origin.startsWith("/services")
      ? t("services") : origin.startsWith("/requests") ? t("requests") : t("today");

  return <div className="min-h-screen safe-bottom" style={{ background: "var(--pc-black)" }} data-testid="player-notification-inbox">
    <div className="max-w-xl mx-auto pb-28">
      <header className="px-5 pt-5 pb-4 safe-top flex items-center gap-3">
        <button className="w-11 h-11 shrink-0 rounded-full grid place-items-center" style={{ background: "var(--pc-charcoal)", border: "1px solid var(--pc-border)" }} onClick={() => leave(origin)} aria-label={t("back")} data-testid="notification-back"><ArrowLeft size={18} /></button>
        <div><p className="text-xs uppercase tracking-[.14em]" style={{ color: "var(--pc-cyan)" }}>{originLabel}</p><h1 className="text-xl font-display font-bold mt-1">{t("notifications")}</h1></div>
      </header>
      <main className="px-5">
        <p className="text-sm mb-4" style={{ color: "var(--pc-muted)" }}>{t("notificationKindsHint")}</p>
        <PlayerPushOptInNotice />
        {state === "loading" && <div className="flex justify-center py-20"><div className="pc-spinner" /></div>}
        {state === "error" && <div className="pc-card p-5"><p>{t("todayError")}</p><button className="pc-btn pc-btn-ghost mt-3" onClick={load}>{t("retry")}</button></div>}
        {state === "ready" && rows.length === 0 && <div className="pc-card p-8 text-center"><Bell size={24} className="mx-auto" color="var(--pc-muted)" /><p className="text-sm mt-3" style={{ color: "var(--pc-muted)" }}>{t("noNotifications")}</p></div>}
        {state === "ready" && rows.length > 0 && <div className="space-y-2">{groupPlayerNotifications(rows).map((group) => {
          const cards = group.rows.map((row) => {
            const action = row.action_status || "information";
            const display = playerNotificationDisplay(row, t);
            const Icon = action === "completed" ? Check : Bell;
            return <button type="button" key={row.id} onClick={() => leave(playerNotificationLink(row.deep_link))} className="pc-card pc-notification-row" data-testid={`notification-${row.id}`} data-action-status={action}>
              <span className="pc-notification-row__icon"><Icon size={17} aria-hidden="true" />{!row.read_at && <span role="img" aria-label={t("notificationUnread")} className="pc-unread-dot" />}</span>
              <span className="min-w-0 flex-1">
                <span className="pc-notification-row__title">{display.title}</span>
                {display.context && <span className="pc-notification-row__context" title={display.context}>{display.context}</span>}
                <span className="pc-notification-row__meta"><span style={{ color: action === "action_required" ? "var(--pc-score-medium)" : undefined }}>{display.statusLabel}</span> · <time dateTime={row.created_at || undefined} title={fmtDateTime(row.created_at, user?.timezone, lang)}>{playerCommunicationTime(row.created_at, user?.timezone, lang)}</time></span>
              </span>
              <ChevronRight size={16} className="self-center shrink-0" color="var(--pc-muted)" />
            </button>;
          });
          return group.rows.length > 1 ? <details key={group.id} className="pc-card pc-notification-plan-group overflow-hidden" data-testid="notification-plan-group">
            <summary>{t("planUpdateGroup")} · {playerPlanGroupDate(group.id, lang)}{group.rows.some((row) => !row.read_at) && <span role="img" aria-label={t("notificationUnread")} className="pc-unread-dot" />}<span className="block text-xs font-normal" style={{ color: "var(--pc-muted)" }}>{group.rows.length} {t("planUpdateCount")}</span></summary>
            <div>{cards}</div>
          </details> : cards[0];
        })}</div>}
      </main>
    </div>
    <PlayerNavigation active="" />
  </div>;
}
